/* eslint-disable functional/immutable-data */
import React, { useEffect } from 'react';
import { Button, Grid, Step, StepContent, StepLabel, Stepper, Typography } from '@mui/material';
import Layout from '~/components/Layout';
import InternalHeader from '~/components/Layout/components/InternalHeader';
import InternalContent from '~/components/Layout/components/InternalContent';
import { useLang } from '~/hooks/useLang';
import { useWorkScheduleForm } from '../hooks/useWorkScheduleForm';
import type { CrewList, WorkScheduleFormSteps } from '../interfaces';
import AssociateShiftAndDate from '../components/AssociateShiftAndDate';
import AssociateDevice from '../components/AssociateDevice';
import AssociateStaff from '../components/AssociateStaff';
import StepActions from '../components/StepActions';
import { LoadingButton } from '@mui/lab';
import { useParams } from 'react-router-dom'
import type ICrudEditParams from '~/components/CrudEdit/interfaces/ICrudEditParams';
import { getWorkScheduleById } from '../services';
import { useHookstate } from '@hookstate/core';


// eslint-disable-next-line max-lines-per-function
export default function Edit() {
  const { translate } = useLang();
  const { id } = useParams<ICrudEditParams>()
  const loading = useHookstate<boolean>(false);
  const {
    startDate,
    endDate,
    initialPeriodCreated,
    workShift,
    device,
    crewPositionId,
    crewList,
    task,
    userId,
    resetValues,
    focusChange,
    crewPositionOptions,
    activeStep,
    disabledNextButton,
    checkAvailabilityResult,
    initialCrewUserIds,
    // functions
    handleCancelButton,
    onPeriodChangeCreated,
    addCrewMember,
    defineEndDateRestHours,
    handleCreateAndEditWorkSchedule,
    handleNext,
    handleBack,
  } = useWorkScheduleForm();
 
  useEffect(() => {
    loading.set(true)
    activeStep.set(2);
    getWorkScheduleById({ id }).then((response) => {
      workShift.set(response.work_shift);
      startDate.set(response.start_date);
      device.set(response.device);
      task.set(response.task);
      mountedCrewList(response)

    }).finally(() => {
      const initialUserIds = crewList.get().map(member => member.id);
      initialCrewUserIds.set(initialUserIds);
      loading.set(false)
    })

  }, []);

  const mountedCrewList = (member) => {
    const crewMembers: CrewList[] = [];
    const getPosition = {
      'Comandante': member?.device?.device_type?.label_commander ? member?.device?.device_type.label_commander : 'Comandante',
      'Motorista': member?.device?.device_type?.label_driver ? member?.device?.device_type.label_driver : 'Motorista',
      'Patrulheiro': member?.device?.device_type?.label_patrolmans ? member?.device?.device_type.label_patrolmans : 'Patrulheiro',
    }

    if (member.commander) {
      crewMembers.push({
        id: member.commander.id,
        representationName: member.commander.representation_name,
        registration: member.commander.registration,
        position: {
          id: 1,
          name: getPosition['Comandante']
        },
        name: member.commander.representation_name
      });
    }

    if (member.driver) {
      crewMembers.push({
        id: member.driver.id,
        representationName: member.driver.representation_name,
        registration: member.driver.registration,
        position: {
          id: 2,
          name: getPosition['Motorista']
        },
        name: member.driver.representation_name
      });
    }

    if (member.patrolmans?.length) {
      const patrolmans = member.patrolmans.map((patrolman) => ({
        id: patrolman.id,
        representationName: patrolman.representation_name,
        registration: patrolman.registration,
        position: {
          id: 3,
          name: getPosition['Patrulheiro']
        },
        name: patrolman.representation_name
      }));
      crewMembers.push(...patrolmans);
    }

    crewList.set(crewMembers);
  }

  const steps: ReadonlyArray<WorkScheduleFormSteps> = [
    {
      id: 0,
      label:
        <Typography variant='h6'>
          {translate('Associate Shift and Date')}
        </Typography>,
      content: <AssociateShiftAndDate
        startDate={startDate}
        endDate={endDate}
        workShift={workShift}
        initialPeriodCreated={initialPeriodCreated}
        onPeriodChangeCreated={onPeriodChangeCreated}
        defineEndDateRestHours={defineEndDateRestHours}
        disabledFields={activeStep.get() !== 0}
      />,
    }, {
      id: 1,
      label:
        <Typography variant='h6'>
          {translate('Associate Device')}
        </Typography>
      ,
      content: <AssociateDevice
        device={device}
        task={task}
        resetValues={resetValues}
        focusChange={focusChange}
        endDate={endDate}
        disabledNextButton={disabledNextButton}
        checkAvailabilityResult={checkAvailabilityResult}
        activeStep={activeStep}
        disabledFields={activeStep.get() !== 1}
        loading={loading.value}
      />,
    }, {
      id: 2,
      label:
        <Typography variant='h6'>
          {translate('Associate Staff')}
        </Typography>,
      content: <AssociateStaff
        crewPositionId={crewPositionId}
        focusChange={focusChange}
        resetValues={resetValues}
        userId={userId}
        device={device}
        crewPositionOptions={crewPositionOptions.get()}
        onAddCrewMember={addCrewMember}
        crewList={crewList}
        activeStep={activeStep}
        checkAvailabilityResult={checkAvailabilityResult}
        disabledFields={activeStep.get() !== 2}
      />,
    }
  ]

  return (
    <Layout marginLayout={true}>
      <InternalHeader title={translate('Edit work schedule')} />
      <InternalContent expandButton={false} >
        <Stepper activeStep={activeStep.get()} orientation='vertical'>
          {steps.map((step, index) => {
            return (
              <Step
                key={step.id}
                expanded={true}
              >
                <StepLabel>
                  {step.label}
                </StepLabel>
                <StepContent TransitionProps={{ unmountOnExit: false }}>
                  {step.content}
                  {activeStep.get() === index && (
                    <StepActions
                      handleNext={handleNext}
                      handleBack={handleBack}
                      index={index}
                      steps={steps}
                      disabledNextButton={disabledNextButton}
                      isEditMode
                    />
                  )}
                </StepContent>
              </Step>
            )
          })}
        </Stepper>
        <Grid container xs={12} justifyContent="flex-end" spacing={2} sx={{ mt: 2 }}>
          <Grid item>
            <Button
              variant='outlined'
              onClick={() => handleCancelButton()}
            >
              {translate('Cancel')}
            </Button>
          </Grid>
          <Grid item>
            <LoadingButton
              variant='contained'
              disabled={activeStep.get() !== steps.length}
              onClick={() => handleCreateAndEditWorkSchedule({ isCreate: false, id: Number(id) })}
            >
              {translate('Save')}
            </LoadingButton>
          </Grid>
        </Grid>

      </InternalContent>
    </Layout >
  )
}